import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Form } from '@components/atoms/Form';
import { RoundProfilePicture } from '@components/atoms/RoundProfilePicture';
import { Space } from '@components/atoms/Space';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useNotifications } from '@hooks/useNotifications';
import { useData } from '@hooks/useData';
import { useSetPrimaryVehicle } from '@services/useSetPrimaryVehicle';
import styles from './ChangePrimaryVehicle.module.css';
import { useRoleChecker } from '@hooks/useRoleChecker';

type Vehicle = {
    uuid?: string;
    brand?: string;
    model?: string;
    plate?: string;
    is_primary?: boolean;
};

export const ChangePrimaryVehicle: FC = () => {
    const { t } = useTranslation();
    const { showDefaultNotification } = useNotifications();
    const { login, user, setUser } = useData();

    const { isDriver } = useRoleChecker();

    const translations = {
        primaryVehicle: t('user-area.primary-vehicle'),
        noVehicle: t('user-area.no-vehicle'),
        selectPrimaryVehicle: t('user-area.select-primary-vehicle'),
        selectPrimaryVehicleHint: t('user-area.select-primary-vehicle-hint'),
    };

    const vehicles: Vehicle[] = user?.data?.vehicles ?? [];
    const currentPrimary = vehicles.find((vehicle) => vehicle.is_primary);

    const { request, loading, lastResponseTime, error, success } = useSetPrimaryVehicle();

    const formRef = useRef<HTMLFormElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);
    const lastRequestedUuid = useRef<string | null>(null);

    const [pickerOpen, setPickerOpen] = useState(false);

    const handleOpenPicker = () => {
        if (!vehicles.length) return;
        setPickerOpen(true);
    };

    const handleSelectVehicle = (uuid?: string) => {
        if (!uuid) return;

        lastRequestedUuid.current = uuid;
        setPickerOpen(false);
        request({ uuid });
    };

    const handleSubmit = async () => {
        return;
    };

    // Close picker on outside click
    useEffect(() => {
        if (!pickerOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                setPickerOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [pickerOpen]);

    useEffect(() => {
        if (!lastResponseTime || !success || !lastRequestedUuid.current) return;

        if (!error && user) {
            showDefaultNotification({ type: 'success', message: success });

            const newPrimary = vehicles.find((vehicle) => vehicle.uuid === lastRequestedUuid.current);

            const updatedVehicles = vehicles.map((vehicle) => ({
                ...vehicle,
                is_primary: vehicle.uuid === lastRequestedUuid.current,
            }));

            setUser({
                ...user,
                data: {
                    ...user.data,
                    vehicles: updatedVehicles,
                    last_vehicle: newPrimary ? { ...newPrimary, is_primary: true } : user.data.last_vehicle,
                },
            });
        } else if (error) {
            showDefaultNotification({ type: 'error', message: error });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    if (!vehicles.length) return null;

    // Render only if user is a Driver
    if (login && !isDriver) return null;

    return (
        <Form formRef={formRef} classes="d-flex flex-column" onSubmit={handleSubmit}>
            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <span
                className="d-flex justify-content-between align-items-center"
                onClick={handleOpenPicker}
                style={{ cursor: 'pointer' }}>
                <Typography fontSize="16" fontWeight="300">
                    <>
                        <Typography fontWeight="300" tagVariant={TypographyTagVariants.span}>
                            <i className="fa-thin fa-car" style={{ marginRight: '5px' }} />
                        </Typography>
                        {translations.primaryVehicle}
                    </>
                </Typography>

                <Typography color="muted">
                    {currentPrimary ? `${currentPrimary.brand ?? ''} ${currentPrimary.model ?? ''}` : translations.noVehicle}
                </Typography>
            </span>

            <>
                {pickerOpen && (
                    <div className={styles.overlay}>
                        <div className={styles.pickerContainer} ref={pickerRef}>
                            <RoundProfilePicture
                                icon="fa-thin fa-car"
                                labelText={translations.selectPrimaryVehicle}
                                hintText={translations.selectPrimaryVehicleHint}
                            />

                            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

                            {vehicles.map((vehicle) => {
                                if (!vehicle.uuid) return null;

                                return (
                                    <React.Fragment key={vehicle.uuid}>
                                        <Button
                                            label={`${vehicle.brand ?? ''} ${vehicle.model ?? ''} // ${vehicle.plate ?? ''}`}
                                            mode={ButtonMode.primary}
                                            classes={styles.button}
                                            disabled={loading || vehicle.uuid === currentPrimary?.uuid}
                                            onClick={() => handleSelectVehicle(vehicle.uuid)}
                                        />
                                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                )}
            </>
            <Space spaceBreakpoints={{ xs: { space: 30 } }} />
        </Form>
    );
};
